import { useState } from 'react'
import { Lock } from 'lucide-react'
import { PERMISSION_GROUPS, PERMISSION_LABELS } from '../../../admin/equipe/equipe-logic'
import { ADMIN_ROLES, ROLE_LABELS, ROLE_PERMISSIONS } from '../../../admin/shared/permissions'
import { Chip } from '../../../admin/shared/ui'
import { FRENTE_KEYS } from '../../contract'
import { FRENTES, frentePii, ROLE_FRENTES } from '../../frentes'
import { CompactRow, HowItWorks, SegmentChips } from '../../ui/calm'
import { rolesOpening } from '../settings-logic'
import { SettingsGroup, SettingsScreen, Switch } from '../ui'

/* Configurações › Pessoas › Permissões e dados pessoais: "vê dados pessoais" por frente (salva em
   settings.frentes[k].pii; vale na hora para as fichas), a matriz perfil × permissão (só leitura: vem de
   PANEL_ADMIN_ROLES + permissions.ts; cada perfil abre a sua frente) e as regras de proteção que não se desligam. */

const FIXED_RULES: [string, string][] = [
    ['Nome completo, data de nascimento, local e ID da criança nunca aparecem', 'em nenhuma frente, nem para administradores. O ERP mostra só o primeiro nome e a inicial.'],
    ['Mostrar um dado pessoal fica registrado', 'quem, quando e de qual doador. Os dados chegam mascarados até a pessoa clicar em “Mostrar”.'],
    ['Contato com a criança só pela OSP', 'cartas, presentes e visitas passam pela organização local. Ninguém fala direto com a criança ou a família.'],
    ['Exportar com dados pessoais pede motivo', 'e só para perfis com essa permissão. Endereços nunca saem.'],
]

export function PermissionsSection() {
    const [group, setGroup] = useState(PERMISSION_GROUPS[0]!.label)
    const g = PERMISSION_GROUPS.find(x => x.label === group) ?? PERMISSION_GROUPS[0]!
    const exportPii = ADMIN_ROLES.filter(r => ROLE_PERMISSIONS[r].includes('export.dados_pessoais')).length
    const refund = ADMIN_ROLES.filter(r => ROLE_PERMISSIONS[r].includes('estorno.aprovar')).length
    return (
        <SettingsScreen
            title="Permissões e dados pessoais"
            head={s => {
                const withPii = FRENTE_KEYS.filter(k => frentePii(k, s)).length
                return {
                    title: `${withPii} de ${FRENTE_KEYS.length} frentes veem dados pessoais, sempre mascarados até “Mostrar”.`,
                    pulse: [
                        { label: exportPii === 1 ? 'perfil exporta dados pessoais' : 'perfis exportam dados pessoais', value: exportPii },
                        { label: refund === 1 ? 'perfil aprova estorno' : 'perfis aprovam estorno', value: refund },
                    ],
                }
            }}
        >
            {(s, save, saving) => (
                <>
                    <SettingsGroup id="cfg-pii" title="Vê dados pessoais, por frente" aside="CPF, e-mail e celular">
                        <ul aria-label="Frentes e dados pessoais" className="-mx-1.5 m-0 flex list-none flex-col p-0">
                            {FRENTE_KEYS.map(k => {
                                const on = frentePii(k, s)
                                return (
                                    <CompactRow
                                        key={k}
                                        title={FRENTES[k].label}
                                        meta={`Abrem: ${rolesOpening(k).map(r => ROLE_LABELS[r]).join(', ') || 'ninguém'}`}
                                        right={<span className="inline-flex flex-wrap justify-end gap-1.5">
                                            {on !== FRENTES[k].pii && <Chip tone="info">fora do padrão</Chip>}
                                            {k === 'tecnologia' && on && <Chip tone="warn">quebra a separação de funções</Chip>}
                                        </span>}
                                        action={<Switch checked={on} disabled={saving} label={`${FRENTES[k].label}: vê dados pessoais`} onChange={v => save({ frentes: { [k]: { pii: v } } })} />}
                                    />
                                )
                            })}
                        </ul>
                    </SettingsGroup>

                    <SettingsGroup id="cfg-roles" title="O que cada perfil pode fazer" aside="só leitura · vem da configuração do servidor">
                        <SegmentChips label="Grupo de permissões" value={group} onChange={setGroup} options={PERMISSION_GROUPS.map(x => ({ value: x.label, label: x.label }))} />
                        <div className="-mx-1 overflow-x-auto">
                            <table className="w-full border-collapse text-[13px]" style={{ minWidth: 220 + g.items.length * 96 }}>
                                <caption className="sr-only">Perfil × permissão: {g.label}</caption>
                                <thead>
                                    <tr className="text-left align-bottom text-[11.5px] text-muted">
                                        <th scope="col" className="px-1 py-1.5 font-semibold">Perfil · frente</th>
                                        {g.items.map(p => <th key={p} scope="col" className="max-w-[120px] px-1 py-1.5 text-center leading-[1.25] font-semibold">{PERMISSION_LABELS[p]}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ADMIN_ROLES.map(role => (
                                        <tr key={role} className="border-t border-line">
                                            <th scope="row" className="h-11 px-1 text-left font-normal">
                                                <span className="block text-[13.5px] font-medium">{ROLE_LABELS[role]}</span>
                                                <span className="block text-[11.5px] text-muted">{role === 'admin' ? 'todas as frentes' : FRENTES[ROLE_FRENTES[role][0]!].label}</span>
                                            </th>
                                            {g.items.map(p => {
                                                const on = ROLE_PERMISSIONS[role].includes(p)
                                                return (
                                                    <td key={p} className="px-1 text-center">
                                                        <span
                                                            role="img"
                                                            aria-label={`${ROLE_LABELS[role]}: ${PERMISSION_LABELS[p]}: ${on ? 'sim' : 'não'}`}
                                                            className="inline-grid h-7 min-w-9 place-items-center rounded-[8px] text-[13px] font-semibold"
                                                            style={on ? { background: 'var(--tint)', color: 'var(--green)' } : { color: 'var(--line-2)' }}
                                                        >{on ? '✓' : '—'}</span>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SettingsGroup>

                    <details className="rounded-[12px] border border-line bg-panel px-3.5 py-2 text-[13px]">
                        <summary className="cursor-pointer font-medium">Regras de proteção · {FIXED_RULES.length} fixas, não se desligam</summary>
                        <ul className="m-0 mt-1.5 flex list-none flex-col p-0">
                            {FIXED_RULES.map(([t, d]) => (
                                <li key={t} className="flex items-start gap-2 border-t border-line py-2 leading-[1.45] first:border-t-0">
                                    <Lock size={14} aria-hidden="true" className="mt-0.5 flex-none text-muted" />
                                    <span><b className="font-semibold">{t}</b>: <span className="text-muted">{d}</span></span>
                                </li>
                            ))}
                        </ul>
                    </details>

                    <HowItWorks>
                        <p className="m-0">Desligado, o cartão de dados pessoais das fichas fica bloqueado para a frente. Cada mudança vale para todas as pessoas da frente e fica na Auditoria.</p>
                        <p className="m-0">A Tecnologia administra o sistema mas, por separação de funções, não vê dados pessoais.</p>
                        <p className="m-0">O perfil de cada pessoa vem da configuração do servidor (PANEL_ADMIN_ROLES) e define a frente que ela abre. Para mudar, fale com a Tecnologia; quem entra sem perfil é administrador.</p>
                    </HowItWorks>
                </>
            )}
        </SettingsScreen>
    )
}
